import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AppTheme from "../themes/AppTheme";
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import "../docs/docs.css";

interface DocEntry {
    id: string;
    title: string;
}

const DOCS: DocEntry[] = [
    { id: "getting-started", title: "Getting Started" },
    { id: "writing-strategies", title: "Writing Strategies" },
    { id: "strategy-api", title: "Strategy API Reference" },
    { id: "backtesting", title: "Running Backtests" },
    { id: "local-websockets", title: "Local IDE Support (Plus/Premium Only)" },
];

const docUrl = (id: string): string => `/docs/${id}.md`;

export const DocsPage: React.FC = () => {
    const [activeId, setActiveId] = React.useState<string>(DOCS[0].id);
    const [contents, setContents] = React.useState<Record<string, string>>({});
    const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [reloadKey, setReloadKey] = React.useState<number>(0);

    React.useEffect(() => {
        let cancelled = false;

        async function loadDocs() {
            try {
                const entries = await Promise.all(
                    DOCS.map(async (doc): Promise<readonly [string, string]> => {
                        const response = await fetch(docUrl(doc.id));
                        if (!response.ok) {
                            throw new Error(`Failed to load ${doc.id} (HTTP ${response.status})`);
                        }
                        return [doc.id, await response.text()];
                    })
                );
                if (!cancelled) {
                    setContents(Object.fromEntries(entries));
                    setIsLoaded(true);
                    setError(null);
                }
            } catch (e) {
                console.error("Failed to load documentation", e);
                if (!cancelled) {
                    setError("Could not load the documentation. Please try again.");
                }
            }
        }

        loadDocs();
        return () => {
            cancelled = true;
        };
    }, [reloadKey]);

    const handleDocSelect = (id: string) => {
        setActiveId(id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const markdownComponents = {
        a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
            const target = DOCS.find((doc) => doc.id === href);
            if (target) {
                return (
                    <Link
                        component="button"
                        type="button"
                        color="primary"
                        underline="hover"
                        sx={{ fontSize: "inherit", fontWeight: 500 }}
                        onClick={() => handleDocSelect(target.id)}
                    >
                        {children}
                    </Link>
                );
            }
            return (
                <a href={href} target="_blank" rel="noreferrer">
                    {children}
                </a>
            );
        },
    };

    return (
        <AppTheme>
            <CssBaseline enableColorScheme />
            <AppAppBar />
            <Box
                sx={{
                    mt: 14,
                    px: { xs: 2, md: 5 },
                    pb: 10,
                    width: "100%",
                    maxWidth: 1400,
                    mx: "auto",
                    display: "flex",
                    gap: 4,
                    alignItems: "flex-start",
                }}
            >
                <Box
                    sx={{
                        display: { xs: "none", md: "block" },
                        width: 260,
                        flexShrink: 0,
                        position: "sticky",
                        top: 96,
                        maxHeight: "calc(100vh - 120px)",
                        overflowY: "auto",
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ px: 1, mb: 1, textTransform: "uppercase", letterSpacing: 1 }}
                    >
                        Documentation
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1 }}>
                        <List dense disablePadding>
                            {DOCS.map((doc) => (
                                <ListItemButton
                                    key={doc.id}
                                    selected={activeId === doc.id}
                                    onClick={() => handleDocSelect(doc.id)}
                                    sx={{ borderRadius: 2, mb: 0.5 }}
                                >
                                    <ListItemText primary={doc.title} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: { xs: "block", md: "none" }, mb: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel id="docs-mobile-select-label">Documentation</InputLabel>
                            <Select
                                labelId="docs-mobile-select-label"
                                id="docs-mobile-select"
                                label="Documentation"
                                size="small"
                                value={activeId}
                                onChange={(event) => handleDocSelect(event.target.value)}
                            >
                                {DOCS.map((doc) => (
                                    <MenuItem key={doc.id} value={doc.id}>
                                        {doc.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box
                        sx={{
                            borderLeft: { xs: 0, md: 1 },
                            borderColor: "divider",
                            pl: { xs: 0, md: 4 },
                        }}
                    >
                        {error !== null ? (
                            <Alert
                                severity="error"
                                action={
                                    <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
                                        Retry
                                    </Button>
                                }
                            >
                                {error}
                            </Alert>
                        ) : !isLoaded ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <div className="docs-markdown">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                    {contents[activeId]}
                                </ReactMarkdown>
                            </div>
                        )}
                    </Box>
                </Box>
            </Box>
            <Footer />
        </AppTheme>
    );
};
